import { addCollection, Icon, type IconifyJSON } from '@iconify/react';
import Box from '@mui/material/Box';
import { useEffect, useState } from 'react';

import { resolveCountryCode } from '@/utils/countryMap';

/**
 * A country's flag, as a circular icon.
 *
 * Replaces the emoji flag `countryFlag()` returns. That one is free but does not
 * exist on Windows: Segoe UI Emoji ships no flag glyphs, so Chrome and Edge fall
 * back to drawing the two regional-indicator letters; a Windows user saw "GB"
 * where everyone else saw a flag. On a B2B product that is most of the seats.
 *
 * The set is bundled, never fetched: `@iconify/react`'s default behaviour is to
 * call `api.iconify.design` for unknown icons, which would send a country code
 * off to a third party on every render of a customer record. `addCollection`
 * registers the whole set locally and the API is never consulted.
 *
 * Loaded on demand, though. The collection is ~440 flags of SVG data, and most
 * screens draw no flag at all; a static import would put it in the entry
 * chunk. The dynamic import lets Vite split it out, and the promise is cached at
 * module level so a page of twenty rows loads it once.
 */
let collection: Promise<void> | null = null;

function loadFlags(): Promise<void> {
  collection ??= import('@iconify-json/circle-flags/icons.json').then((module) => {
    addCollection(module.default as IconifyJSON);
  });
  return collection;
}

export interface CountryFlagProps {
  /** A country name or ISO code: whatever the record happens to hold. */
  country: string | null | undefined;
  /** Edge length in px. Default 16, the size that sits with body text. */
  size?: number;
}

export default function CountryFlag({ country, size = 16 }: CountryFlagProps) {
  const code = country ? resolveCountryCode(country) : null;
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!code) return;
    let alive = true;
    void loadFlags().then(() => {
      if (alive) setReady(true);
    });
    return () => {
      alive = false;
    };
  }, [code]);

  // No code, no flag, and never a placeholder standing in for one. The country
  // is always written out beside this, so a missing flag costs nothing; a wrong
  // or generic one would claim something.
  if (!code) return null;

  if (!ready) {
    // Holds the space so the line does not jump when the set arrives.
    return (
      <Box
        component="span"
        sx={{ width: size, height: size, borderRadius: '50%', bgcolor: 'action.hover', flexShrink: 0 }}
      />
    );
  }

  return (
    <Box
      component={Icon}
      icon={`circle-flags:${code.toLowerCase()}`}
      aria-hidden
      sx={{ width: size, height: size, flexShrink: 0, display: 'block' }}
    />
  );
}
